/**
 * The single place the frontend talks to the API.
 *
 * This replaces 146 hand-rolled `fetch` calls spread over 32 files, each of
 * which rebuilt the base URL, the auth header, and its own idea of what a
 * failure means. That produced 21 byte-identical copies of getAuthHeaders, 26
 * swallowing `catch` blocks (15 of them empty), and 27 calls that never checked
 * `res.ok`.
 *
 * The cost of that was not theoretical. /apply called an authenticated endpoint
 * for months; every request returned 401, and `res.ok ? await res.json() : []`
 * turned the failure into an empty list. The attorney typeahead silently found
 * nobody and no error was ever raised.
 *
 * Two rules follow from that:
 *   1. A non-OK response always throws. Callers opt in to tolerating failure;
 *      they never get it by default.
 *   2. The thrown error carries the status and the API's `detail` string,
 *      because the UI renders `detail` straight into toasts.
 */

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOKEN_KEY = 'access_token';

/** Error carrying HTTP status alongside the message. `status` is 0 for network failures. */
export class ApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Authorization header, or `{}` when signed out.
 *
 * Returning an empty object rather than `{ Authorization: null }` matters: the
 * hand-rolled copies did the same, and sending a null header makes requests
 * that should be anonymous look malformed instead.
 */
export const getAuthHeaders = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Session policy, applied in one place instead of 146.
 *
 * 401 means the session is genuinely gone, so the dead token is cleared.
 * 403 does NOT: the caller is authenticated and simply not permitted, and
 * discarding their session over a permissions error would log people out for
 * clicking the wrong thing. 5xx and network failures are transient by
 * definition and must never cost someone their session — a backend blip
 * logging every user out is worse than the blip.
 */
const applySessionPolicy = (status) => {
  if (status === 401 && typeof window !== 'undefined') {
    localStorage.removeItem(TOKEN_KEY);
  }
};

const parseJsonSafely = async (res) => {
  try {
    return await res.json();
  } catch {
    return null;
  }
};

async function request(method, path, body, options = {}) {
  const { headers: extraHeaders, ...rest } = options;

  const headers = { ...getAuthHeaders(), ...extraHeaders };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      credentials: 'include',
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...rest,
    });
  } catch (err) {
    // Network-level failure: no response at all. Status 0 lets callers tell
    // this apart from an HTTP error without string-matching.
    throw new ApiError(err?.message || 'Network request failed', 0, null);
  }

  if (!res.ok) {
    applySessionPolicy(res.status);
    const parsed = await parseJsonSafely(res);
    const message = parsed?.detail || `Request failed (${res.status})`;
    throw new ApiError(message, res.status, parsed);
  }

  if (res.status === 204) return null;
  return parseJsonSafely(res);
}

export const apiGet = (path, options) => request('GET', path, undefined, options);

/**
 * Fetch a binary body (PDF downloads).
 *
 * Same auth, same session policy, same error shape as the JSON helpers — the
 * only difference is that the success path returns a Blob instead of parsed
 * JSON. Without this the two download sites would have to hand-roll their own
 * fetch again, and would silently sit outside the 401 policy.
 */
export async function apiBlob(path, options = {}) {
  const { headers: extraHeaders, ...rest } = options;
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: 'GET',
      credentials: 'include',
      headers: { ...getAuthHeaders(), ...extraHeaders },
      ...rest,
    });
  } catch (err) {
    throw new ApiError(err?.message || 'Network request failed', 0, null);
  }
  if (!res.ok) {
    applySessionPolicy(res.status);
    const parsed = await parseJsonSafely(res);
    throw new ApiError(parsed?.detail || `Request failed (${res.status})`, res.status, parsed);
  }
  return res.blob();
}
export const apiPost = (path, body, options) => request('POST', path, body, options);
export const apiPut = (path, body, options) => request('PUT', path, body, options);
export const apiDelete = (path, options) => request('DELETE', path, undefined, options);

const api = { apiGet, apiPost, apiPut, apiDelete, apiBlob, ApiError, getAuthHeaders };
export default api;
